/**
 * Shared order-dedup heuristic used by the Profit Report and Truth
 * Profitability Report order tables. Both pages independently derive
 * MergedOrderRecord-shaped rows from truth data and need to collapse:
 *   1. Duplicate rows sharing the same display order_id.
 *   2. Same Day Tracker fragments — opaque per-line IDs with no stable
 *      display value — via a (date, customer, origin, destination) key.
 *
 * Row shapes differ between callers (raw postal_code/city fields vs a
 * single combined string, different date-preference for the dedup key),
 * so this is parameterized by field accessors rather than a fixed type —
 * only the dedup algorithm itself (richness scoring, opaque-id detection,
 * two-pass collapse) is shared.
 */

/** True when orderId is a raw entity ULID or Same Day Tracker fingerprint with no dedup value. */
export function isOpaqueOrderId(orderId: string): boolean {
  // ord_-prefixed entity ULIDs (internal form)
  if (/^ord_[0-9a-z]{26}$/i.test(orderId)) return true;
  // Raw 26-char Crockford base32 ULIDs used directly as display id (e.g. 01KY7EX6Y594TN7664WG7RE055)
  if (/^[0-9A-HJKMNP-TV-Z]{26}$/.test(orderId)) return true;
  // Same Day Tracker fingerprint: darcica_sdt:{date}:{24-hex}
  if (/^darcica_sdt:[^:]+:[0-9a-f]{24}$/i.test(orderId)) return true;
  // Legacy SDT line-number form: darcica_sdt:{date}:{integer}
  if (/^darcica_sdt:[^:]+:\d+$/i.test(orderId)) return true;
  return false;
}

export interface OrderDedupAccessors<T> {
  orderId: (r: T) => string;
  customer: (r: T) => string | null | undefined;
  /** Date used for the composite dedup key — callers may prefer origin or destination date. */
  date: (r: T) => string | null | undefined;
  origin: (r: T) => string | null | undefined;
  destination: (r: T) => string | null | undefined;
  revenue: (r: T) => number | null | undefined;
  vehicleAssigned: (r: T) => unknown;
  vehicleObserved: (r: T) => unknown;
}

/** Richness score for dedup: vehicle_assigned (2) + vehicle_observed (1) + non-empty fields (tiebreak). */
function recordRichnessScore<T>(r: T, accessors: OrderDedupAccessors<T>): number {
  let s = 0;
  if (accessors.vehicleAssigned(r)) s += 2;
  if (accessors.vehicleObserved(r)) s += 1;
  for (const v of Object.values(r as Record<string, unknown>)) {
    if (typeof v === 'string' && v.trim()) s += 0.01;
    else if (typeof v === 'number' && v !== 0) s += 0.01;
  }
  return s;
}

/** Composite key for opaque-id rows: (date, customer, origin, destination).
 *  Falls back to (date, customer, revenue) when route data is absent. */
function compositeKey<T>(r: T, accessors: OrderDedupAccessors<T>): string | null {
  const date = (accessors.date(r) || '').slice(0, 10);
  const customer = (accessors.customer(r) ?? '').trim().toLowerCase();
  if (!date || !customer) return null;
  const origin = (accessors.origin(r) ?? '').trim().toLowerCase();
  const destination = (accessors.destination(r) ?? '').trim().toLowerCase();
  if (origin || destination) return `${date}|${customer}|${origin}|${destination}`;
  // Fallback: no route data — use revenue as a proxy discriminator
  const revenue = accessors.revenue(r);
  return `${date}|${customer}|rev:${revenue != null ? String(Math.round(Number(revenue))) : ''}`;
}

/**
 * Deduplicate by orderId (pass 1), then by composite key for opaque-id rows (pass 2).
 * Pass 2 catches Same Day Tracker fragments with different fingerprints but same route/day.
 */
export function deduplicateOrders<T>(
  records: T[],
  accessors: OrderDedupAccessors<T>,
  onDropEmptyId?: (r: T) => void,
): T[] {
  // Pass 1: human-readable order_id
  const byId = new Map<string, T>();
  for (const r of records) {
    const key = accessors.orderId(r);
    if (!key) {
      onDropEmptyId?.(r);
      continue;
    }
    const existing = byId.get(key);
    if (!existing || recordRichnessScore(r, accessors) > recordRichnessScore(existing, accessors)) {
      byId.set(key, r);
    }
  }

  // Pass 2: composite key for opaque survivors
  const clean: T[] = [];
  const byComposite = new Map<string, T>();
  for (const r of byId.values()) {
    if (!isOpaqueOrderId(accessors.orderId(r))) { clean.push(r); continue; }
    const ck = compositeKey(r, accessors);
    if (!ck) { clean.push(r); continue; }
    const existing = byComposite.get(ck);
    if (!existing || recordRichnessScore(r, accessors) > recordRichnessScore(existing, accessors)) {
      byComposite.set(ck, r);
    }
  }

  return [...clean, ...byComposite.values()];
}
